/**
 * 主应用组件
 * 供需罗盘门店版
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout, message, Spin, Button, Dropdown, Avatar, Space, Modal, Drawer, Slider, Input, List, Empty, Select } from 'antd';
import { UserOutlined, LogoutOutlined, KeyOutlined, SaveOutlined, StarOutlined, DeleteOutlined, CloseOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import RegionSelector from './components/RegionSelector';
import FilterPanel from './components/FilterPanel';
import MapView from './components/MapView';
import StoreCompassTable from './components/StoreCompassTable';
import AiStrategyDrawer from './components/AiStrategyDrawer';
import Login from './components/Login';
import ChangePasswordModal from './components/ChangePasswordModal';
import { getRegions, getStoreTypes, getStores, getBoundaries, getCategories, getDemandData, getPopulationData, getIncomeData, checkAuth, logout as logoutApi } from './services/api';
import { buildCompositeDemandData, enrichStores, getEffectiveWeights, getStoreKey } from './utils/storeDemand';
import { filterStoresByPlatform, getPlatformStatus } from './utils/platformFilter';
import './App.css';

const { Sider, Content } = Layout;

function App() {
  // 登录态
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [authChecked, setAuthChecked] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  // 启动时检查登录状态
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const result = await checkAuth();
        if (result.logged_in) {
          setLoggedIn(true);
          setUsername(result.username);
        } else {
          setLoggedIn(false);
        }
      } catch (error) {
        setLoggedIn(false);
      } finally {
        setAuthChecked(true);
      }
    };
    verifyAuth();
  }, []);

  // 处理登录成功
  const handleLoginSuccess = (name) => {
    setLoggedIn(true);
    setUsername(name);
  };

  // 处理退出登录
  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      // 忽略错误，前端仍退出
    }
    setLoggedIn(false);
    setUsername('');
  };

  // 登录态检查完成前显示 loading
  if (!authChecked) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw' }}>
        <Spin size="large" tip="正在加载..." />
      </div>
    );
  }

  // 未登录显示登录页
  if (!loggedIn) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <>
      <MainApp username={username} onLogout={handleLogout} onOpenPasswordModal={() => setPasswordModalOpen(true)} />
      <ChangePasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
    </>
  );
}

function MainApp({ username, onLogout, onOpenPasswordModal }) {
  // 右上角用户下拉菜单
  const userMenuItems = [
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: onOpenPasswordModal,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: onLogout,
    },
  ];

  const [regions, setRegions] = useState({ provinces: [], cities: [], districts: [], streets: [] });
  const [storeTypes, setStoreTypes] = useState([]);
  const [stores, setStores] = useState([]);
  const [boundaries, setBoundaries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const initializationStartedRef = React.useRef(false);
  const [meituanConfidence, setMeituanConfidence] = useState(0.6);
  const [elemeConfidence, setElemeConfidence] = useState(0.6);
  const [supplyLayerEnabled, setSupplyLayerEnabled] = useState(true);
  const [currentFilters, setCurrentFilters] = useState({});
  const [currentLimit] = useState(100);
  const [shouldAutoFit, setShouldAutoFit] = useState(true);
  const [selectedStoreKeys, setSelectedStoreKeys] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const defaultWeights = { category: 34, age: 33, income: 33, landmark: 0, scene: 0 };
  const [weights, setWeights] = useState(defaultWeights);
  const [draftWeights, setDraftWeights] = useState(weights);
  const [weightOpen, setWeightOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupSource, setGroupSource] = useState('compass');
  const [groupDescription, setGroupDescription] = useState('');
  const [savedGroups, setSavedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('storeCompassGroups') || '[]'); }
    catch { return []; }
  });

  // 需求图层相关状态
  const [categories, setCategories] = useState([]);
  const [demandLayerEnabled, setDemandLayerEnabled] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [demandData, setDemandData] = useState([]);

  // 人口图层相关状态
  const [populationLayerEnabled, setPopulationLayerEnabled] = useState(false);
  const [selectedPopulationTypes, setSelectedPopulationTypes] = useState(['youth', 'labor', 'elderly']); // 默认全选=总人口
  const [populationData, setPopulationData] = useState([]);

  // 收入图层相关状态
  const [incomeLayerEnabled, setIncomeLayerEnabled] = useState(false);
  const [selectedIncomeLevel, setSelectedIncomeLevel] = useState('high'); // 单选，默认高收入
  const [incomeData, setIncomeData] = useState([]);
  const [enabledDimensions, setEnabledDimensions] = useState({ category: true, age: true, income: true, landmark: false, scene: false });
  const [mapLayer, setMapLayer] = useState('composite');

  // 数据表展开/收缩状态
  const [tableExpanded, setTableExpanded] = useState(true);

  const enrichedStores = useMemo(() => enrichStores({
    stores, demandData, populationData, incomeData, weights, selectedCategory,
    meituanConfidence, elemeConfidence, enabledDimensions,
  }), [stores, demandData, populationData, incomeData, weights, selectedCategory, meituanConfidence, elemeConfidence, enabledDimensions]);

  const compositeDemandData = useMemo(() => buildCompositeDemandData({
    demandData, populationData, incomeData, weights, enabledDimensions,
  }), [demandData, populationData, incomeData, weights, enabledDimensions]);

  const effectiveDraftWeights = useMemo(() => getEffectiveWeights(draftWeights, {
    category: demandData.length > 0,
    age: populationData.length > 0,
    income: incomeData.length > 0,
    landmark: false,
    scene: false,
  }, enabledDimensions), [draftWeights, demandData, populationData, incomeData, enabledDimensions]);

  // 下钻控制状态（用于同步RegionSelector）
  const [drillDownRegion, setDrillDownRegion] = useState({
    province: undefined,
    city: undefined,
    district: undefined,
    street: undefined,
  });

  // 使用 ref 存储最新的筛选条件
  const currentFiltersRef = React.useRef({});
  const supplyLayerEnabledRef = React.useRef(true);
  const storeRequestRef = React.useRef(0);
  const viewportRequestRef = React.useRef(0);
  const regionRequestRef = React.useRef(0);
  const suppressViewportRequestsRef = React.useRef(false);
  const resumeViewportTimeoutRef = React.useRef(null);

  const selectedStoresForAi = useMemo(() => enrichStores({
    stores: selectedStores,
    demandData,
    populationData,
    incomeData,
    weights,
    selectedCategory,
    meituanConfidence,
    elemeConfidence,
    enabledDimensions,
  }), [selectedStores, demandData, populationData, incomeData, weights, selectedCategory,
    meituanConfidence, elemeConfidence, enabledDimensions]);

  // 初始化数据
  useEffect(() => {
    if (initializationStartedRef.current) return;
    initializationStartedRef.current = true;

    const initData = async () => {
      try {
        setLoading(true);
        const [regionsData, typesData, categoriesData] = await Promise.all([
          getRegions(),
          getStoreTypes(),
          getCategories(),
        ]);

        const safeRegionsData = {
          provinces: Array.isArray(regionsData?.provinces) ? regionsData.provinces : [],
          cities: Array.isArray(regionsData?.cities) ? regionsData.cities : [],
          districts: Array.isArray(regionsData?.districts) ? regionsData.districts : [],
          streets: Array.isArray(regionsData?.streets) ? regionsData.streets : [],
        };

        setRegions(safeRegionsData);
        setStoreTypes(Array.isArray(typesData) ? typesData : []);
        setCategories(Array.isArray(categoriesData?.categories) ? categoriesData.categories : []);
        setBoundaries([]);
        const initialFilters = {
          parentTypes: [],
          childTypes: [],
          operatingFirstCategories: [],
          operatingSecondCategories: [],
          o2oSource: 'all',
          meituanConfidence: 0.6,
          elemeConfidence: 0.6,
        };
        setCurrentFilters(initialFilters);
        currentFiltersRef.current = initialFilters;

        setStores([]);
        setTotalCount(0);
        message.success('数据加载成功');
      } catch (error) {
        console.error('初始化失败:', error);
        message.error('数据加载失败，请检查网络连接');
        setRegions({ provinces: [], cities: [], districts: [], streets: [] });
        setStoreTypes([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, []);

  // 处理地区筛选变化（从顶部地区选择器）
  const handleRegionChange = async (regionFilters) => {
    const regionRequestId = ++regionRequestRef.current;
    const storeRequestId = ++storeRequestRef.current;
    viewportRequestRef.current += 1;
    suppressViewportRequestsRef.current = true;
    if (viewChangeTimeoutRef.current) clearTimeout(viewChangeTimeoutRef.current);
    if (resumeViewportTimeoutRef.current) clearTimeout(resumeViewportTimeoutRef.current);

    try {
      setLoading(true);
      setSelectedStoreKeys([]);
      setSelectedStores([]);
      setShouldAutoFit(true);
      setDrillDownRegion({
        province: regionFilters.province || null,
        city: regionFilters.city || null,
        district: regionFilters.district || null,
        street: regionFilters.street || null,
      });
      // Do not display stores from the previous region under the new boundary/loading state.
      setStores([]);
      setTotalCount(0);

      // 合并地区筛选和其他筛选条件
      const newFilters = {
        ...regionFilters,
        parentTypes: currentFilters.parentTypes || [],
        childTypes: currentFilters.childTypes || [],
        operatingFirstCategories: currentFilters.operatingFirstCategories || [],
        operatingSecondCategories: currentFilters.operatingSecondCategories || [],
        o2oSource: currentFilters.o2oSource || 'all',
        meituanConfidence: currentFilters.meituanConfidence ?? meituanConfidence,
        elemeConfidence: currentFilters.elemeConfidence ?? elemeConfidence,
      };

      // 保存当前筛选条件
      setCurrentFilters(newFilters);
      currentFiltersRef.current = newFilters;

      // 全国层级只展示底图，门店点位后续由独立数据源提供。
      if (!regionFilters.province) {
        setBoundaries([]);
        setStores([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      // 确定limit值
      let actualLimit;
      actualLimit = currentLimit;

      const filtersWithLimit = { ...newFilters, limit: actualLimit };

      // 边界和门店互不依赖，并行请求可显著缩短区域切换等待时间。
      const [boundariesData, storesData] = await Promise.all([
        getBoundaries(regionFilters),
        supplyLayerEnabledRef.current ? getStores(filtersWithLimit) : Promise.resolve(null),
      ]);
      if (
        regionRequestId !== regionRequestRef.current
        || storeRequestId !== storeRequestRef.current
      ) return;
      setBoundaries(boundariesData.boundaries || []);

      // 如果供给图层开启，获取门店数据
      if (supplyLayerEnabledRef.current && storesData) {
        const filteredStores = filterStoresByPlatform(
          storesData.stores,
          meituanConfidence,
          elemeConfidence,
          newFilters.o2oSource || 'all'
        );

        setStores(filteredStores);
        setTotalCount(storesData.total || 0);

        const meituanCount = filteredStores.filter(s =>
          getPlatformStatus(s, meituanConfidence, elemeConfidence).onMeituan
        ).length;
        const elemeCount = filteredStores.filter(s =>
          getPlatformStatus(s, meituanConfidence, elemeConfidence).onEleme
        ).length;

        message.success(
          `已加载 ${storesData.total} 家门店（美团${meituanCount}家，饿了么${elemeCount}家）和 ${boundariesData.boundaries?.length || 0} 个边界`
        );
      } else {
        setStores([]);
        setTotalCount(0);
        message.success(`已加载 ${boundariesData.boundaries?.length || 0} 个行政区边界`);
      }

      // 地图核心数据已经就绪，先结束全屏遮罩。画像数据在后台并行补充。
      setLoading(false);

      const profileRequests = [];

      // 注意：即使选择了街道，需求图层仍显示所属区县的所有街道数据。
      if ((enabledDimensions.category || demandLayerEnabled) && selectedCategory && selectedCategory.length > 0) {
        // 解析多选的品类
        const firstCategories = [];
        const secondCategories = [];
        selectedCategory.forEach(cat => {
          if (cat && cat.length > 0) {
            if (!firstCategories.includes(cat[0])) {
              firstCategories.push(cat[0]);
            }
            if (cat.length > 1 && !secondCategories.includes(cat[1])) {
              secondCategories.push(cat[1]);
            }
          }
        });

        const demandFilters = {
          province: regionFilters.province,
          city: regionFilters.city,
          district: regionFilters.district,
          // 不传 street，这样会显示整个区县的需求分布
          first_categories: firstCategories,
          second_categories: secondCategories,
        };
        profileRequests.push(getDemandData(demandFilters).then(demandResponse => {
          if (regionRequestId === regionRequestRef.current) setDemandData(demandResponse.data || []);
        }).catch(demandError => console.error('加载需求数据失败:', demandError)));
      }

      if ((enabledDimensions.age || populationLayerEnabled) && selectedPopulationTypes && selectedPopulationTypes.length > 0) {
        const popFilters = {
          province: regionFilters.province,
          city: regionFilters.city,
          district: regionFilters.district,
          population_types: selectedPopulationTypes,
        };
        profileRequests.push(getPopulationData(popFilters).then(popResponse => {
          if (regionRequestId === regionRequestRef.current) setPopulationData(popResponse.data || []);
        }).catch(popError => console.error('加载人口数据失败:', popError)));
      }

      if ((enabledDimensions.income || incomeLayerEnabled) && selectedIncomeLevel) {
        const incomeFilters = {
          province: regionFilters.province,
          city: regionFilters.city,
          district: regionFilters.district,
          income_level: selectedIncomeLevel,
        };
        profileRequests.push(getIncomeData(incomeFilters).then(incomeResponse => {
          if (regionRequestId === regionRequestRef.current) setIncomeData(incomeResponse.data || []);
        }).catch(incomeError => console.error('加载收入数据失败:', incomeError)));
      }

      await Promise.all(profileRequests);

    } catch (error) {
      if (regionRequestId !== regionRequestRef.current) return;
      console.error('获取数据失败:', error);
      message.error('获取数据失败');
    } finally {
      if (
        regionRequestId === regionRequestRef.current
        && storeRequestId === storeRequestRef.current
      ) {
        setLoading(false);
        // setFitView emits moveend after boundary rendering. Ignore that programmatic event;
        // later user drags/zooms continue to use viewport-based loading normally.
        resumeViewportTimeoutRef.current = setTimeout(() => {
          suppressViewportRequestsRef.current = false;
        }, 1800);
      }
    }
  };

  // 处理图层设置变化（从左侧面板）
  const handleApplyFilter = async (filterSettings) => {
    const storeRequestId = ++storeRequestRef.current;
    // 主动筛选应产生一批新随机样本，取消之前尚未完成的视野请求。
    viewportRequestRef.current += 1;
    if (viewChangeTimeoutRef.current) {
      clearTimeout(viewChangeTimeoutRef.current);
      viewChangeTimeoutRef.current = null;
    }
    setSelectedStoreKeys([]);
    setSelectedStores([]);

    // 更新置信度阈值
    if (filterSettings.meituanConfidence !== undefined) {
      setMeituanConfidence(filterSettings.meituanConfidence);
    }
    if (filterSettings.elemeConfidence !== undefined) {
      setElemeConfidence(filterSettings.elemeConfidence);
    }

    // 如果供给图层开启，加载门店数据（无论是否选择了地区）
    if (supplyLayerEnabledRef.current) {
      try {
        setLoading(true);

        const newFilters = {
          ...currentFilters,
          parentTypes: filterSettings.parentTypes || [],
          childTypes: filterSettings.childTypes || [],
          operatingFirstCategories: filterSettings.operatingFirstCategories || [],
          operatingSecondCategories: filterSettings.operatingSecondCategories || [],
          o2oSource: filterSettings.o2oSource || 'all',
          meituanConfidence: filterSettings.meituanConfidence ?? meituanConfidence,
          elemeConfidence: filterSettings.elemeConfidence ?? elemeConfidence,
        };

        setCurrentFilters(newFilters);
        currentFiltersRef.current = newFilters;

        if (!newFilters.province) {
          setStores([]);
          setTotalCount(0);
          return;
        }

        const actualLimit = currentLimit;
        const filtersWithLimit = {
          ...newFilters,
          limit: actualLimit,
        };

        const storesData = await getStores(filtersWithLimit);
        if (storeRequestId !== storeRequestRef.current) return;

        const meituanThreshold = filterSettings.meituanConfidence ?? meituanConfidence;
        const elemeThreshold = filterSettings.elemeConfidence ?? elemeConfidence;

        const filteredStores = filterStoresByPlatform(
          storesData.stores,
          meituanThreshold,
          elemeThreshold,
          filterSettings.o2oSource || 'all'
        );

        setStores(filteredStores);
        setTotalCount(storesData.total || 0);
        await Promise.all([
          loadPopulationData(selectedPopulationTypes, newFilters, true),
          loadIncomeData(selectedIncomeLevel, newFilters, true),
        ]);
        if (storeRequestId === storeRequestRef.current) {
          message.success(`已应用筛选，加载 ${filteredStores.length} 家门店`);
        }
      } catch (error) {
        if (storeRequestId !== storeRequestRef.current) return;
        console.error('应用筛选失败:', error);
        message.error('应用筛选失败');
      } finally {
        if (storeRequestId === storeRequestRef.current) setLoading(false);
      }
    }
  };

  // 保留引用用于取消旧请求。地图拖拽和缩放不再重新随机抽取门店；
  // 门店数据只在地区或筛选条件明确改变时更新。
  const viewChangeTimeoutRef = React.useRef(null);

  const handleAutoFitComplete = useCallback(() => {
    viewportRequestRef.current += 1;
    if (viewChangeTimeoutRef.current) {
      clearTimeout(viewChangeTimeoutRef.current);
      viewChangeTimeoutRef.current = null;
    }
    setShouldAutoFit(false);
  }, []);

  // 专题图总开关：关闭只取消地图着色，保留当前专题选择。
  const handleDemandLayerToggle = async (enabled) => {
    setDemandLayerEnabled(enabled);
    setPopulationLayerEnabled(enabled && mapLayer === 'population');
    setIncomeLayerEnabled(enabled && mapLayer === 'income');

    if (!enabled) {
      return;
    }

    // 如果没有选择省份，加载全国省份的边界数据
    if (!currentFilters.province && boundaries.length === 0) {
      try {
        setLoading(true);
        const boundariesData = await getBoundaries({});
        setBoundaries(boundariesData.boundaries || []);
      } catch (error) {
        console.error('加载全国边界数据失败:', error);
      } finally {
        setLoading(false);
      }
    }

    if (mapLayer === 'demand' && selectedCategory?.length) await loadDemandData();
    if (mapLayer === 'population' && selectedPopulationTypes?.length) await loadPopulationData();
    if (mapLayer === 'income' && selectedIncomeLevel) await loadIncomeData();
    if (mapLayer === 'composite') {
      await Promise.all([
        enabledDimensions.category && selectedCategory?.length ? loadDemandData() : Promise.resolve(),
        enabledDimensions.age && selectedPopulationTypes?.length ? loadPopulationData() : Promise.resolve(),
        enabledDimensions.income && selectedIncomeLevel ? loadIncomeData() : Promise.resolve(),
      ]);
    }
  };

  // 处理人口图层开关变化（需要互斥逻辑）
  const handlePopulationLayerToggle = async (enabled) => {
    setPopulationLayerEnabled(enabled);

    if (!enabled) {
      return;
    }

    setDemandLayerEnabled(false);
    setIncomeLayerEnabled(false);

    // 如果没有选择省份，加载全国省份的边界数据
    if (!currentFilters.province && boundaries.length === 0) {
      try {
        setLoading(true);
        const boundariesData = await getBoundaries({});
        setBoundaries(boundariesData.boundaries || []);
      } catch (error) {
        console.error('加载全国边界数据失败:', error);
      } finally {
        setLoading(false);
      }
    }

    // 加载人口数据
    if (selectedPopulationTypes && selectedPopulationTypes.length > 0) {
      await loadPopulationData();
    }
  };

  // 处理收入图层开关变化（需要互斥逻辑）
  const handleIncomeLayerToggle = async (enabled) => {
    setIncomeLayerEnabled(enabled);

    if (!enabled) {
      return;
    }

    setDemandLayerEnabled(false);
    setPopulationLayerEnabled(false);

    // 如果没有选择省份，加载全国省份的边界数据
    if (!currentFilters.province && boundaries.length === 0) {
      try {
        setLoading(true);
        const boundariesData = await getBoundaries({});
        setBoundaries(boundariesData.boundaries || []);
      } catch (error) {
        console.error('加载全国边界数据失败:', error);
      } finally {
        setLoading(false);
      }
    }

    // 加载收入数据
    await loadIncomeData();
  };

  const handleMapLayerChange = async (layer) => {
    viewportRequestRef.current += 1;
    if (viewChangeTimeoutRef.current) {
      clearTimeout(viewChangeTimeoutRef.current);
      viewChangeTimeoutRef.current = null;
    }
    setMapLayer(layer);
    setPopulationLayerEnabled(demandLayerEnabled && layer === 'population');
    setIncomeLayerEnabled(demandLayerEnabled && layer === 'income');

    if (!demandLayerEnabled) return;

    // 全国普通底图使用高德原生行政区；专题图需要省级多边形才能按数值着色。
    if (!currentFilters.province) {
      if (layer === 'none') {
        setBoundaries([]);
        return;
      }
      if (boundaries.length === 0) {
        try {
          setLoading(true);
          const boundariesData = await getBoundaries({});
          setBoundaries(boundariesData.boundaries || []);
        } catch (error) {
          console.error('加载全国专题边界失败:', error);
          message.error('全国专题边界加载失败');
          setLoading(false);
          return;
        } finally {
          setLoading(false);
        }
      }
    }

    if (layer === 'demand' && selectedCategory?.length) await loadDemandData();
    if (layer === 'population' && selectedPopulationTypes?.length) await loadPopulationData();
    if (layer === 'income' && selectedIncomeLevel) await loadIncomeData();
    if (layer === 'composite') {
      await Promise.all([
        enabledDimensions.category && selectedCategory?.length ? loadDemandData() : Promise.resolve(),
        enabledDimensions.age && selectedPopulationTypes?.length ? loadPopulationData() : Promise.resolve(),
        enabledDimensions.income && selectedIncomeLevel ? loadIncomeData() : Promise.resolve(),
      ]);
    }
  };

  const handleDimensionToggle = async (dimension, enabled) => {
    setEnabledDimensions(current => ({ ...current, [dimension]: enabled }));
    if (!enabled) return;
    if (dimension === 'category' && selectedCategory?.length) await loadDemandData();
    if (dimension === 'age' && selectedPopulationTypes?.length) await loadPopulationData();
    if (dimension === 'income' && selectedIncomeLevel) await loadIncomeData();
  };

  // 处理供给图层开关变化
  const handleSupplyLayerToggle = async (enabled) => {
    storeRequestRef.current += 1;
    setSupplyLayerEnabled(enabled);
    supplyLayerEnabledRef.current = enabled;

    if (!enabled) {
      setStores([]);
      setTotalCount(0);
      setSelectedStoreKeys([]);
      setSelectedStores([]);
      return;
    }

    if (!currentFiltersRef.current.province) {
      setStores([]);
      setTotalCount(0);
      return;
    }

    await handleRegionChange(currentFiltersRef.current);
  };

  // 处理品类选择变化
  const handleCategoryChange = async (category) => {
    setSelectedCategory(category);

    if ((enabledDimensions.category || demandLayerEnabled) && category && category.length > 0) {
      // 如果没有选择省份，加载全国省份的边界数据
      if (!currentFilters.province && boundaries.length === 0) {
        try {
          setLoading(true);
          const boundariesData = await getBoundaries({});
          setBoundaries(boundariesData.boundaries || []);
        } catch (error) {
          console.error('加载全国边界数据失败:', error);
        } finally {
          setLoading(false);
        }
      }

      await loadDemandData(category);
    } else {
      setDemandData([]);
    }
  };

  // 加载需求数据
  const loadDemandData = async (category = null) => {
    try {
      setLoading(true);

      const targetCategory = category || selectedCategory;

      // 多选模式：targetCategory 是二维数组，如 [['一级1', '二级1'], ['一级2']]
      if (!targetCategory || targetCategory.length === 0) {
        setDemandData([]);
        return;
      }

      // 解析多选的品类
      const firstCategories = [];
      const secondCategories = [];
      targetCategory.forEach(cat => {
        if (cat && cat.length > 0) {
          if (!firstCategories.includes(cat[0])) {
            firstCategories.push(cat[0]);
          }
          if (cat.length > 1 && !secondCategories.includes(cat[1])) {
            secondCategories.push(cat[1]);
          }
        }
      });

      // 注意：即使选择了街道，需求图层仍显示所属区县的所有街道数据
      // 所以不传 street 参数
      const filters = {
        province: currentFilters.province,
        city: currentFilters.city,
        district: currentFilters.district,
        // 不传 street，这样会显示整个区县的需求分布
        first_categories: firstCategories,
        second_categories: secondCategories,
      };

      const response = await getDemandData(filters);
      setDemandData(response.data || []);

      if (response.data && response.data.length > 0) {
        message.success(`已加载 ${response.data.length} 个区域的需求数据`);
      } else {
        message.warning('当前筛选条件下没有需求数据');
      }

    } catch (error) {
      console.error('加载需求数据失败:', error);
      message.error('加载需求数据失败');
      setDemandData([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理人口类型选择变化
  const handlePopulationTypesChange = async (types) => {
    setSelectedPopulationTypes(types);
    if ((enabledDimensions.age || populationLayerEnabled) && types && types.length > 0) {
      await loadPopulationData(types);
    } else if (!types || types.length === 0) {
      setPopulationData([]);
    }
  };

  // 处理收入层级选择变化
  const handleIncomeLevelChange = async (level) => {
    setSelectedIncomeLevel(level);
    if ((enabledDimensions.income || incomeLayerEnabled) && level) {
      await loadIncomeData(level);
    }
  };

  // 加载人口数据
  const loadPopulationData = async (types = null, filtersOverride = null, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const targetTypes = types || selectedPopulationTypes;
      const sourceFilters = filtersOverride || currentFilters;

      const filters = {
        province: sourceFilters.province,
        city: sourceFilters.city,
        district: sourceFilters.district,
        population_types: targetTypes,
      };

      const response = await getPopulationData(filters);
      setPopulationData(response.data || []);

      if (!silent && response.data && response.data.length > 0) {
        const typeLabel = response.type_label || '人口';
        message.success(`已加载 ${response.data.length} 个区域的${typeLabel}数据`);
      } else if (!silent) {
        message.warning('当前筛选条件下没有人口数据');
      }
    } catch (error) {
      console.error('加载人口数据失败:', error);
      if (!silent) message.error('加载人口数据失败');
      setPopulationData([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // 加载收入数据
  const loadIncomeData = async (level = null, filtersOverride = null, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const targetLevel = level || selectedIncomeLevel;
      const sourceFilters = filtersOverride || currentFilters;

      const filters = {
        province: sourceFilters.province,
        city: sourceFilters.city,
        district: sourceFilters.district,
        income_level: targetLevel,
      };

      const response = await getIncomeData(filters);
      setIncomeData(response.data || []);

      const levelNames = { high: '高收入', middle: '中收入', low: '低收入' };
      if (!silent && response.data && response.data.length > 0) {
        message.success(`已加载 ${response.data.length} 个区域的${levelNames[targetLevel]}人口数据`);
      } else if (!silent) {
        message.warning('当前筛选条件下没有收入数据');
      }
    } catch (error) {
      console.error('加载收入数据失败:', error);
      if (!silent) message.error('加载收入数据失败');
      setIncomeData([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // 处理地图双击下钻
  const handleDrillDown = (boundary) => {
    if (!boundary) return;

    // 判断当前边界层级，下钻到下一级
    const hasStreet = boundary.street && boundary.street.trim() !== '';
    const hasDistrict = boundary.district && boundary.district.trim() !== '';
    const hasCity = boundary.city && boundary.city.trim() !== '';
    const hasProvince = boundary.province && boundary.province.trim() !== '';

    let newRegion = {};

    if (hasStreet) {
      // 双击街道边界，选中该街道单独高亮显示
      newRegion = {
        province: boundary.province,
        city: boundary.city,
        district: boundary.district,
        street: boundary.street,  // 选中该街道
      };
    } else if (hasDistrict) {
      // 当前是区县级别的边界，下钻显示该区县的街道列表
      newRegion = {
        province: boundary.province,
        city: boundary.city,
        district: boundary.district,
        street: null,  // 不选择具体街道，显示该区县的所有街道
      };
    } else if (hasCity) {
      // 当前是城市级别的边界，下钻显示该城市的区县列表
      newRegion = {
        province: boundary.province,
        city: boundary.city,
        district: null,  // 不选择具体区县，显示该城市的所有区县
        street: null,
      };
    } else if (hasProvince) {
      // 当前是省份级别的边界，下钻显示该省的城市列表
      newRegion = {
        province: boundary.province,
        city: null,  // 不选择具体城市，显示该省的所有城市
        district: null,
        street: null,
      };
    } else {
      return;
    }

    // 更新下钻控制状态（同步RegionSelector）
    setDrillDownRegion({
      province: newRegion.province,
      city: newRegion.city,
      district: newRegion.district,
      street: newRegion.street,
    });

    // 触发区域筛选
    handleRegionChange(newRegion);
  };

  // 处理返回上一级
  const handleGoBack = () => {
    const { province, city, district, street } = drillDownRegion;

    let newRegion = {};

    if (street) {
      // 当前在街道级别，返回到区县级别
      newRegion = {
        province,
        city,
        district,
        street: null,
      };
    } else if (district) {
      // 当前在区县级别，返回到城市级别
      newRegion = {
        province,
        city,
        district: null,
        street: null,
      };
    } else if (city) {
      // 当前在城市级别，返回到省份级别
      newRegion = {
        province,
        city: null,
        district: null,
        street: null,
      };
    } else if (province) {
      // 当前在省份级别，返回到全国级别
      newRegion = {
        province: null,
        city: null,
        district: null,
        street: null,
      };
    } else {
      // 已经在全国级别，无法返回
      message.info('已经是最高级别');
      return;
    }

    // 更新下钻控制状态
    setDrillDownRegion({
      province: newRegion.province,
      city: newRegion.city,
      district: newRegion.district,
      street: newRegion.street,
    });

    // 触发区域筛选
    handleRegionChange(newRegion);
  };

  const handleSelectionChange = (keys, rows) => {
    // A checkbox click must invalidate any delayed bounds response. Otherwise
    // an older map request can appear to be caused by selecting this row.
    viewportRequestRef.current += 1;
    if (viewChangeTimeoutRef.current) {
      clearTimeout(viewChangeTimeoutRef.current);
      viewChangeTimeoutRef.current = null;
    }
    setSelectedStoreKeys(keys);
    setSelectedStores(rows);
  };

  const handleTableInteraction = useCallback(() => {
    // Reading or scrolling the table must never allow an older map-bounds
    // request to replace the rows currently being inspected.
    viewportRequestRef.current += 1;
    if (viewChangeTimeoutRef.current) {
      clearTimeout(viewChangeTimeoutRef.current);
      viewChangeTimeoutRef.current = null;
    }
  }, []);

  const handleStoreToggle = useCallback(store => {
    viewportRequestRef.current += 1;
    if (viewChangeTimeoutRef.current) {
      clearTimeout(viewChangeTimeoutRef.current);
      viewChangeTimeoutRef.current = null;
    }
    setSelectedStoreKeys(previous => {
      const selected = previous.includes(store._key);
      const next = selected ? previous.filter(key => key !== store._key) : [...previous, store._key];
      setSelectedStores(enrichedStores.filter(item => next.includes(item._key)));
      return next;
    });
  }, [enrichedStores]);

  const saveStoreGroup = () => {
    const name = groupName.trim();
    if (!name) {
      message.warning('请输入门店组名称');
      return;
    }
    if (!selectedStores.length) {
      message.warning('请先从数据表选择门店');
      return;
    }
    const next = [{
      id: Date.now(),
      name,
      createdAt: new Date().toLocaleString('zh-CN'),
      source: groupSource,
      description: groupDescription.trim(),
      weights,
      filters: { selectedCategory, selectedPopulationTypes, selectedIncomeLevel },
      stores: selectedStores,
    }, ...savedGroups];
    localStorage.setItem('storeCompassGroups', JSON.stringify(next));
    setSavedGroups(next);
    setGroupName('');
    setGroupDescription('');
    setSaveOpen(false);
    message.success('门店组已保存在当前浏览器');
  };

  const loadStoreGroup = (group, keepCurrent = false) => {
    if (group.weights) setWeights(group.weights);
    if (group.filters) {
      setSelectedCategory(group.filters.selectedCategory || null);
      setSelectedPopulationTypes(group.filters.selectedPopulationTypes || ['youth', 'labor', 'elderly']);
      setSelectedIncomeLevel(group.filters.selectedIncomeLevel || 'high');
    }
    const groupKeys = (group.stores || []).map(store => store._key || getStoreKey(store));
    const keys = keepCurrent ? [...new Set([...selectedStoreKeys, ...groupKeys])] : groupKeys;
    setSelectedStoreKeys(keys);
    setSelectedStores(enrichedStores.filter(store => keys.includes(store._key)));
    setHistoryOpen(false);
    message.success(keepCurrent ? '已在当前圈选上叠加历史门店' : '已加载历史门店组');
  };

  const changeDraftWeight = (key, value) => {
    const dimensions = ['category', 'age', 'income'];
    const others = dimensions.filter(item => item !== key);
    const safeValue = Math.max(0, Math.min(100, Math.round(value)));
    const remaining = 100 - safeValue;
    const safeOtherWeights = others.map(item => Math.max(0, Number(draftWeights[item]) || 0));
    const otherTotal = safeOtherWeights.reduce((sum, item) => sum + item, 0);
    const next = { ...draftWeights, landmark: 0, scene: 0, [key]: safeValue };
    if (otherTotal === 0) {
      const base = Math.floor(remaining / others.length);
      others.forEach((item, index) => { next[item] = base + (index < remaining % others.length ? 1 : 0); });
    } else {
      const allocations = others.map((item, index) => {
        const exact = remaining * safeOtherWeights[index] / otherTotal;
        return { item, value: Math.floor(exact), fraction: exact - Math.floor(exact) };
      });
      let leftover = remaining - allocations.reduce((sum, allocation) => sum + allocation.value, 0);
      [...allocations].sort((a, b) => b.fraction - a.fraction).forEach(allocation => {
        if (leftover > 0) { allocation.value += 1; leftover -= 1; }
      });
      allocations.forEach(allocation => { next[allocation.item] = allocation.value; });
    }
    setDraftWeights(next);
  };

  const removeStoreGroup = id => {
    const next = savedGroups.filter(group => group.id !== id);
    localStorage.setItem('storeCompassGroups', JSON.stringify(next));
    setSavedGroups(next);
  };

  const applyWeights = () => {
    const sum = Object.values(draftWeights).reduce((total, value) => total + value, 0);
    if (sum !== 100 || Object.values(draftWeights).some(value => value < 0 || value > 100)) {
      message.warning('每项权重必须在 0–100% 之间，且五项合计为 100%');
      return;
    }
    setWeights(draftWeights);
    setWeightOpen(false);
    message.success('需求权重已应用');
  };

  return (
    <Layout className="compass-app">
      <header className="compass-toolbar">
        <RegionSelector
          regions={regions}
          onFilterChange={handleRegionChange}
          controlledProvince={drillDownRegion.province}
          controlledCity={drillDownRegion.city}
          controlledDistrict={drillDownRegion.district}
          controlledStreet={drillDownRegion.street}
        />
        <div className="toolbar-actions">
          <span className="selected-badge">✓ 已选 {selectedStoreKeys.length} 家门店</span>
          <Button disabled={!selectedStoreKeys.length} onClick={() => { setSelectedStoreKeys([]); setSelectedStores([]); }}>清空选择</Button>
          <Button type="primary" icon={<SaveOutlined />} disabled={!selectedStoreKeys.length} onClick={() => setSaveOpen(true)}>保存门店组</Button>
          <Button className="ai-button" icon={<StarOutlined />} disabled={!selectedStoreKeys.length} onClick={() => setAiOpen(true)}>AI做功策略</Button>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space className="user-entry"><Avatar icon={<UserOutlined />} />{username}</Space>
          </Dropdown>
        </div>
      </header>

      <Layout className="compass-body">
        <Sider width={336} className="compass-sider">
          <FilterPanel
            storeTypes={storeTypes}
            categories={categories}
            supplyLayerEnabled={supplyLayerEnabled}
            demandLayerEnabled={demandLayerEnabled}
            historyCount={savedGroups.length}
            selectedCategory={selectedCategory}
            populationLayerEnabled={populationLayerEnabled}
            selectedPopulationTypes={selectedPopulationTypes}
            incomeLayerEnabled={incomeLayerEnabled}
            selectedIncomeLevel={selectedIncomeLevel}
            mapLayer={mapLayer}
            onApplyFilter={handleApplyFilter}
            onSupplyLayerToggle={handleSupplyLayerToggle}
            onDemandLayerToggle={handleDemandLayerToggle}
            onCategoryChange={handleCategoryChange}
            onPopulationLayerToggle={handlePopulationLayerToggle}
            onPopulationTypesChange={handlePopulationTypesChange}
            onIncomeLayerToggle={handleIncomeLayerToggle}
            onIncomeLevelChange={handleIncomeLevelChange}
            onMapLayerChange={handleMapLayerChange}
            onOpenWeights={() => {
              const safeWeights = Object.fromEntries(Object.entries(weights).map(([key, value]) => [key, Math.max(0, Number(value) || 0)]));
              const activeTotal = ['category', 'age', 'income'].reduce((sum, key) => sum + safeWeights[key], 0);
              if (activeTotal > 0) {
                const category = Math.round(safeWeights.category / activeTotal * 100);
                const age = Math.round(safeWeights.age / activeTotal * 100);
                setDraftWeights({ category, age, income: 100 - category - age, landmark: 0, scene: 0 });
              } else {
                setDraftWeights(defaultWeights);
              }
              setWeightOpen(true);
            }}
            onOpenHistory={() => setHistoryOpen(true)}
          />
        </Sider>

        <Content className="compass-content">
          <div className="map-stage">
            <MapView
              stores={enrichedStores}
              selectedStoreKeys={selectedStoreKeys}
              onStoreToggle={handleStoreToggle}
              boundaries={boundaries}
              totalCount={totalCount}
              meituanConfidence={meituanConfidence}
              elemeConfidence={elemeConfidence}
              shouldAutoFit={shouldAutoFit}
              onAutoFitComplete={handleAutoFitComplete}
              demandLayerEnabled={demandLayerEnabled && (mapLayer === 'demand' || mapLayer === 'composite')}
              demandData={mapLayer === 'composite' ? compositeDemandData : demandData}
              demandLayerMode={mapLayer}
              selectedCategory={selectedCategory}
              populationLayerEnabled={populationLayerEnabled}
              populationData={populationData}
              selectedPopulationTypes={selectedPopulationTypes}
              incomeLayerEnabled={incomeLayerEnabled}
              incomeData={incomeData}
              selectedIncomeLevel={selectedIncomeLevel}
              onDrillDown={handleDrillDown}
              onGoBack={handleGoBack}
              currentRegion={drillDownRegion}
              loading={loading}
              nationalOverview={!currentFilters.province}
            />
            {selectedStoreKeys.length > 0 && <div className="selection-float">已圈选 <b>{selectedStoreKeys.length}</b> 家高价值门店</div>}
          </div>
          {currentFilters.province && <div className={`table-dock ${tableExpanded ? 'expanded' : ''}`}>
            <button className="table-dock-header" onClick={() => setTableExpanded(!tableExpanded)}>
              数据表 <b>（{enrichedStores.length}家门店）</b><span>{tableExpanded ? '收起' : '展开'}</span>
            </button>
            <div
              className="table-dock-content"
              onWheelCapture={handleTableInteraction}
              onPointerDownCapture={handleTableInteraction}
              onScrollCapture={handleTableInteraction}
            >
              <StoreCompassTable stores={enrichedStores} loading={loading} selectedKeys={selectedStoreKeys}
                onSelectionChange={handleSelectionChange} />
            </div>
          </div>}
        </Content>
      </Layout>

      <Modal className="weight-modal" title={<span className="weight-modal-title"><span>⚙️</span>需求维度权重设置</span>}
        open={weightOpen} maskClosable={false} onCancel={() => setWeightOpen(false)} footer={[
        <Button key="reset" onClick={() => setDraftWeights(defaultWeights)}>恢复默认</Button>,
        <Button key="cancel" onClick={() => setWeightOpen(false)}>取消</Button>,
        <Button key="apply" type="primary" onClick={applyWeights}>应用</Button>,
      ]}>
        <p className="modal-help">拖动滑块调整各维度权重，三项合计始终为 100%。权重为 0% 的维度不参与需求指数计算。</p>
        <WeightSlider color="#ef4444" label={`品类需求${!selectedCategory?.length ? '（未选择品类，暂不生效）' : ''}`} value={draftWeights.category} onChange={value => changeDraftWeight('category', value)} />
        <WeightSlider color="#f59e0b" label="年龄分层" value={draftWeights.age} onChange={value => changeDraftWeight('age', value)} />
        <WeightSlider color="#10b981" label="收入分布" value={draftWeights.income} onChange={value => changeDraftWeight('income', value)} />
        <WeightSlider color="#3b82f6" label="地标标签" value={0} disabled />
        <WeightSlider color="#8b5cf6" label="场景标签" value={0} disabled />
        <div className="weight-summary">
          <div className="weight-total">配置权重总和：<b>{Object.values(draftWeights).reduce((sum, value) => sum + value, 0)}%</b></div>
          <div className="weight-total">当前实际生效：<b>{['category', 'age', 'income'].map(key => `${key === 'category' ? '品类' : key === 'age' ? '年龄' : '收入'} ${Math.round(effectiveDraftWeights[key] * 100)}%`).join(' / ')}</b></div>
        </div>
        {!selectedCategory?.length && <p className="modal-help">品类实际权重为 0%，是因为尚未选择“需求品类”；选择后加载到品类需求数据才会参与评分。</p>}
      </Modal>

      <Modal className="save-group-modal" width={720}
        title={<span className="save-modal-title"><SaveOutlined />保存门店组</span>}
        open={saveOpen} onCancel={() => setSaveOpen(false)} onOk={saveStoreGroup}
        okText="确认保存" cancelText="取消">
        <div className="save-summary">
          <div><b>{selectedStoreKeys.length}</b><span>选中门店数</span></div>
          <div><b>{new Set(selectedStores.map(store => store.street).filter(Boolean)).size}</b><span>覆盖街道</span></div>
          <div><b>{new Set(selectedStores.map(store => store.childType || store.parentType).filter(Boolean)).size}</b><span>业态类型</span></div>
        </div>
        <label className="save-field-label">门店组名称<em>*</em></label>
        <Input className="save-control" value={groupName} onChange={e => setGroupName(e.target.value)}
          placeholder="例如：朝阳区高潜便利店拓展清单-2026Q3" maxLength={40} />
        <label className="save-field-label">门店组来源</label>
        <Select className="save-control" value={groupSource} onChange={setGroupSource}
          options={[
            { value: 'compass', label: '供需罗盘圈选' },
            { value: 'existing', label: '已有门店组扩充' },
            { value: 'upload', label: '外部文件上传' },
          ]} />
        <label className="save-field-label">门店组描述</label>
        <Input.TextArea className="save-control save-description" value={groupDescription}
          onChange={e => setGroupDescription(e.target.value)}
          placeholder="说明该门店组的用途、目标品牌或做功计划...（选填）" maxLength={200} />
      </Modal>

      <Drawer className="history-drawer" title={<span className="history-drawer-title">📋 <b>历史清单</b></span>}
        placement="left" open={historyOpen} onClose={() => setHistoryOpen(false)} width={570}
        closeIcon={<CloseOutlined />}>
        {savedGroups.length ? <List dataSource={savedGroups} renderItem={group => {
          const groupStores = group.stores || [];
          const averageDemand = groupStores.length
            ? Math.round(groupStores.reduce((sum, store) => sum + (Number(store.demandScore) || 0), 0) / groupStores.length)
            : 0;
          const groupWeights = group.weights || defaultWeights;
          const weightLabels = [
            ['category', '品类需求'], ['age', '年龄分层'], ['income', '收入分布'],
            ['landmark', '地标标签'], ['scene', '场景标签'],
          ];
          return <List.Item className="history-card">
            <div className="history-card-head"><strong>{group.name}</strong><time>{group.createdAt}</time></div>
            <div className="history-card-meta">
              <span>📍 {groupStores.length}家门店</span>
              <span>🗺️ {new Set(groupStores.map(store => store.street).filter(Boolean)).size}个街道</span>
              <span>📊 平均指数 {averageDemand}</span>
            </div>
            <div className="history-weight-tags">{weightLabels.map(([key, label]) =>
              <span key={key}>{label}:{Number(groupWeights[key]) || 0}%</span>)}</div>
            {group.description && <p className="history-description">{group.description}</p>}
            <div className="history-card-actions">
              <Button type="primary" icon={<DownloadOutlined />} onClick={() => loadStoreGroup(group)}>加载</Button>
              <Button icon={<CopyOutlined />} onClick={() => loadStoreGroup(group, true)}>基于此新建</Button>
              <Button className="history-delete" danger icon={<DeleteOutlined />} onClick={() => removeStoreGroup(group.id)}>删除</Button>
            </div>
          </List.Item>;
        }} /> : <Empty description="当前浏览器还没有保存门店组" />}
      </Drawer>

      <AiStrategyDrawer open={aiOpen} onClose={() => setAiOpen(false)} stores={selectedStoresForAi}
        selectedCategory={selectedCategory} weights={weights} />
    </Layout>
  );
}

const WeightSlider = ({ label, value, onChange, disabled, color }) => (
  <div className="weight-row"><div><span className="weight-label"><i style={{ backgroundColor: color }} />{label}</span><b>{value}%</b></div>
    <Slider min={0} max={100} value={value} onChange={onChange} disabled={disabled} /></div>
);

export default App;
